import { getAnalytics, logEvent, Analytics } from 'firebase/analytics';
import { getFirebaseApp, isFirebaseConfigured } from './firebase';

// Get analytics instance from the shared Firebase app
let analyticsInstance: Analytics | null = null;

export const initializeMonitoring = async () => {
  try {
    // Only initialize if Firebase is configured
    if (!isFirebaseConfigured()) {
      console.warn('[Monitoring] Firebase not configured, skipping analytics initialization');
      return;
    }

    // Get the existing Firebase app (already initialized by firebase.ts)
    const app = getFirebaseApp();
    if (!app) {
      console.warn('[Monitoring] Firebase app not available');
      return;
    }

    // Initialize Analytics with the shared app
    try {
      analyticsInstance = getAnalytics(app);
      console.log('[Monitoring] Analytics initialized successfully');
    } catch (error: any) {
      // Analytics might already be initialized, which is fine
      if (error?.code === 'analytics/already-initialized') {
        console.warn('[Monitoring] Analytics already initialized');
        // Try to get the existing instance
        analyticsInstance = getAnalytics(app);
      } else {
        console.warn('[Monitoring] Failed to initialize analytics:', error?.message);
      }
    }
  } catch (error) {
    console.warn('[Monitoring] Monitoring initialization warning:', error);
  }
};

// Analytics event tracking utilities
export const trackEvent = (eventName: string, eventParams?: Record<string, any>) => {
  try {
    if (analyticsInstance) {
      logEvent(analyticsInstance, eventName, eventParams);
    }
  } catch (error) {
    console.warn('[Monitoring] Failed to log analytics event:', error);
  }
};

// Track page views
export const trackPageView = (pageName: string) => {
  trackEvent('page_view', {
    page_title: pageName,
    page_location: window.location.href,
  });
};

// Track user actions
export const trackUserAction = (action: string, details?: Record<string, any>) => {
  trackEvent('user_action', {
    action,
    ...details,
  });
};

// Track game top-up events
export const trackTopUp = (gameId: string, amount: number, currency: string) => {
  trackEvent('topup_initiated', {
    game_id: gameId,
    amount,
    currency,
    timestamp: new Date().toISOString(),
  });
};

// Track purchase completion
export const trackPurchaseComplete = (orderId: string, amount: number, currency: string) => {
  trackEvent('purchase_complete', {
    transaction_id: orderId,
    value: amount,
    currency,
    timestamp: new Date().toISOString(),
  });
};

// Track errors for Crashlytics
export const trackError = (error: Error, context?: string) => {
  try {
    if (analyticsInstance) {
      logEvent(analyticsInstance, 'app_error', {
        error_message: error.message,
        error_stack: error.stack,
        context,
        timestamp: new Date().toISOString(),
      });
    }
    console.error(`[${context || 'Error'}]`, error);
  } catch (e) {
    console.warn('[Monitoring] Failed to track error:', e);
  }
};

// Export analytics instance for direct use if needed
export const getAnalyticsInstance = () => analyticsInstance;
